import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "js-cookie";
import { FaArrowLeftLong } from "react-icons/fa6";

const API = process.env.REACT_APP_API_BASE_URL;

interface Product {
  id: number;
  product_code: string;
  name: string;
  price: string;
  commission_percent: string;
  stock: string;
  category_id: string;
  description: string;
  status: string;
  discount: string;
  sizes: string[];
  colors: string[];
  created_at: string;
  updated_at: string;
}

interface Order {
  id: number;
  order_code: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  note: string | null;
  product_id: string;
  affiliate_id: string;
  quantity: string;
  price: string;
  total_price: string;
  status: string;
  created_at: string;
  updated_at: string;
  product: Product;
}

interface SalesResponse {
  affiliate: string;
  total_orders: number;
  total_revenue: string;
  total_commission: number;
  current_page: number;
  per_page: number;
  last_page: number;
  orders: Order[];
}

export default function DetailPenjualanAffiliatePage() {
  const { id } = useParams();
  const [data, setData] = useState<SalesResponse | null>(null);
  const [page, setPage] = useState(1);

  const fetchSales = async () => {
    try {
      const token = Cookies.get("token");

      const res = await axios.get(`${API}/api/admin/affiliates/${id}/sales`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { page },
      });

      setData(res.data);
    } catch {
      Swal.fire("Error", "Gagal mengambil data penjualan", "error");
    }
  };

  useEffect(() => {
    fetchSales();
  }, [page, id]);

  if (!data) return <div>Loading...</div>;

  return (
    <div className="mt-7">
      <div className="flex justify-items-start gap-3 items-center mb-6 text-2xl">
        <Link to={"/dashboard/affiliate/penjualan"}>
          <FaArrowLeftLong />
        </Link>
        <h1 className="text-2xl font-bold ">Detail Penjualan Affiliate</h1>
      </div>

      {/* KOTAK-KOTAK SUMMARY */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <h2 className="text-gray-500 text-sm">Affiliate</h2>
          <p className="text-lg font-bold">{data.affiliate}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <h2 className="text-gray-500 text-sm">Total Orders</h2>
          <p className="text-lg font-bold">{data.total_orders}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <h2 className="text-gray-500 text-sm">Total Revenue</h2>
          <p className="text-lg font-bold">
            Rp
            {new Intl.NumberFormat("id-ID").format(Number(data.total_revenue))}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 text-center">
          <h2 className="text-gray-500 text-sm">Total Commission</h2>
          <p className="text-lg font-bold">Rp {new Intl.NumberFormat("id-ID").format(Number(data.total_commission))} </p>
        </div>
      </div>

      {/* TABLE ORDERS */}
      <div className="bg-white rounded-xl shadow border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left">Order Code</th>
              <th className="p-3 text-left">Customer</th>
              <th className="p-3 text-left">Phone</th>
              <th className="p-3 text-left">Product</th>
              <th className="p-3 text-left">Quantity</th>
              <th className="p-3 text-left">Price</th>
              <th className="p-3 text-left">Total</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Date</th>
            </tr>
          </thead>

          <tbody>
            {data.orders.map((order) => (
              <tr key={order.id} className="border-t">
                <td className="p-3 font-mono">{order.order_code}</td>
                <td className="p-3">{order.name}</td>
                <td className="p-3">{order.phone}</td>
                <td className="p-3">{order.product.name}</td>
                <td className="p-3">{order.quantity}</td>
                <td className="p-3">Rp {new Intl.NumberFormat("id-ID").format(Number(order.price))}</td>
                <td className="p-3">Rp {new Intl.NumberFormat("id-ID").format(Number(order.total_price))}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded text-xs ${order.status === "paid" ? "bg-green-100 text-green-700" : order.status === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>{order.status}</span>
                </td>
                <td className="p-3">{new Date(order.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <div className="flex justify-end gap-2 mt-4">
        {[...Array(data.last_page)].map((_, i) => (
          <button key={i} onClick={() => setPage(i + 1)} className={`px-3 py-1 rounded ${page === i + 1 ? "bg-primary text-white" : "bg-gray-200"}`}>
            {i + 1}
          </button>
        ))}
      </div>
    </div>
  );
}
